import React, { useState, useEffect, useMemo } from 'react';
import { Building, MoreVertical, Trash2, Download, Eye, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input, Select } from 'antd';
import { fetchTermSheets, deleteTermSheet, generateTermSheetPDF } from '../utils/api';
import { isViewer } from '../utils/auth';

const TermSheets = ({ currentUser }) => {
  const [termSheets, setTermSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuState, setMenuState] = useState({ id: null, top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadTermSheets();
  }, []);

  const loadTermSheets = async () => {
    try {
      setLoading(true);
      const data = await fetchTermSheets();
      setTermSheets(data);
      setError(null);
    } catch (err) {
      setError('Failed to load term sheets');
      console.error('Error loading term sheets:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeMenu = () => setMenuState({ id: null, top: 0, left: 0 });

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this term sheet?')) {
      try {
        await deleteTermSheet(id);
        setTermSheets(termSheets.filter(ts => ts._id !== id));
      } catch (err) {
        alert('Failed to delete term sheet');
        console.error('Error deleting term sheet:', err);
      } finally {
        closeMenu();
      }
    }
  };

  const handleGeneratePDF = async (id) => {
    try {
      const blob = await generateTermSheetPDF(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `term-sheet-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to generate PDF');
      console.error('Error generating PDF:', err);
    } finally {
      closeMenu();
    }
  };

  const toggleMenu = (event, id) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const padding = 8;
    const left = Math.max(8, rect.right - menuWidth);
    const top = rect.bottom + padding;
    setMenuState(prev => (prev.id === id ? { id: null, top: 0, left: 0 } : { id, top, left }));
  };

  useEffect(() => {
    const onDocClick = () => closeMenu();
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const filteredTermSheets = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return termSheets.filter(ts => {
      const matchesStatus = statusFilter === 'all' || ts.status === statusFilter;
      if (!matchesStatus) return false;
      if (!term) return true;

      return [
        ts.companyName,
        ts.subLesseeName,
        ts.registeredName,
        ts.termSheetNumber
      ].some(field => (field || '').toLowerCase().includes(term));
    });
  }, [termSheets, searchTerm, statusFilter]);

  const renderStatusBadge = (status) => {
    const palette = {
      draft: { bg: '#fef3c7', color: '#b45309' },
      active: { bg: '#dcfce7', color: '#15803d' },
      completed: { bg: '#dbeafe', color: '#1d4ed8' },
      terminated: { bg: '#fee2e2', color: '#b91c1c' }
    };
    const styles = palette[status] || { bg: '#e5e7eb', color: '#4b5563' };
    return (
      <span
        style={{
          background: styles.bg,
          color: styles.color,
          padding: '4px 10px',
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'capitalize'
        }}
      >
        {status || 'unknown'}
      </span>
    );
  };

  const renderEmptyState = () => (
    <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
      <div
        style={{
          maxWidth: 420,
          width: '100%',
          background: 'linear-gradient(135deg, #f9fafb 0%, #eef2ff 100%)',
          borderRadius: 24,
          padding: '40px 36px',
          boxShadow: '0 20px 45px rgba(79,70,229,0.18)',
          border: '1px solid rgba(79,70,229,0.15)',
          textAlign: 'center'
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 20,
            margin: '0 auto 20px auto',
            background: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4f46e5'
          }}
        >
          <Building size={34} />
        </div>
        <h2 style={{ margin: '0 0 12px 0', color: '#1f2937', fontSize: 24 }}>No Term Sheets Yet</h2>
        <p style={{ color: '#4b5563', fontSize: 14, marginBottom: 28 }}>
          Term sheets are generated automatically when you convert a lead. Come back here once those flows are completed.
        </p>
        <button
          className="primary-btn"
          onClick={() => window.location.assign('/leads')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            background: '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            padding: '12px 22px',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 12px 28px rgba(79,70,229,0.25)'
          }}
        >
          Review Leads
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div className="page-title">
          <h1>Term Sheets</h1>
          <p>Manage sub-lease agreements, download PDFs, and keep lease records updated</p>
        </div>
        <div className="page-actions" style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
          <div className="search-input" style={{ minWidth: 240 }}>
            <Input
              prefix={<Search size="1.2em" color="#6b7280" />}
              size="large"
              allowClear
              placeholder="Search by company, lessee, or number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="search-input" style={{ minWidth: 200 }}>
            <Select
              size="large"
              allowClear
              placeholder="All statuses"
              value={statusFilter === 'all' ? undefined : statusFilter}
              onChange={(value) => setStatusFilter(value || 'all')}
              options={[
                { label: 'Draft', value: 'draft' },
                { label: 'Active', value: 'active' },
                { label: 'Completed', value: 'completed' },
                { label: 'Terminated', value: 'terminated' }
              ]}
            />
          </div>
        </div>
      </div>

      {error && (
        <div style={{ 
          background: '#fee2e2', 
          color: '#dc2626', 
          padding: '12px 16px', 
          borderRadius: '12px', 
          marginBottom: '20px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {filteredTermSheets.length === 0 ? (
        renderEmptyState()
      ) : (
        <div className="table-container">
          <div className="table-wrapper" style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 16px 40px rgba(15,23,42,0.08)' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 220 }}>Company</th>
                  <th>Sub-Lessee</th>
                  <th>Rentable Seats</th>
                  <th>Monthly Rent</th>
                  <th>Rent Start</th>
                  <th>Handover</th>
                  <th>Status</th>
                  <th style={{ width: 64, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTermSheets.map((termSheet) => (
                  <tr key={termSheet._id}>
                    <td>
                      <div className="table-company-cell">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building size={16} color="#6b7280" />
                          <Link to={`/term-sheets/${termSheet._id}`} className="table-company-link">{termSheet.companyName}</Link>
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{termSheet.registeredName}</div>
                      </div>
                    </td>
                    <td>{termSheet.subLesseeName}</td>
                    <td>{termSheet.rentableSeats}</td>
                    <td>{formatCurrency(termSheet.monthlyRent)}</td>
                    <td>{formatDate(termSheet.rentStartDate)}</td>
                    <td>{formatDate(termSheet.handoverDate)}</td>
                    <td>{renderStatusBadge(termSheet.status)}</td>
                    <td style={{ textAlign: 'right', position: 'relative' }}>
                      <button
                        onClick={(e) => toggleMenu(e, termSheet._id)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '8px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        aria-label="Open actions menu"
                      >
                        <MoreVertical size={18} color="#6b7280" />
                      </button>
                      {menuState.id === termSheet._id && (
                        <div
                          style={{
                            position: 'fixed',
                            top: menuState.top,
                            left: menuState.left,
                            minWidth: 220,
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
                            padding: 8,
                            zIndex: 2000,
                            border: '1px solid #e2e8f0'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            to={`/term-sheets/${termSheet._id}`}
                            onClick={closeMenu}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '10px 12px',
                              borderRadius: 8,
                              color: '#1f2937',
                              textDecoration: 'none',
                              fontSize: 14
                            }}
                          >
                            <Eye size={16} /> View Details
                          </Link>
                          <button
                            onClick={() => handleGeneratePDF(termSheet._id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '10px 12px',
                              width: '100%',
                              borderRadius: 8,
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: '#1f2937'
                            }}
                          >
                            <Download size={16} /> Download PDF
                          </button>
                          {!isViewer() && (
                          <button
                            onClick={() => handleDelete(termSheet._id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '10px 12px',
                              width: '100%',
                              borderRadius: 8,
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              fontSize: 14,
                              color: '#dc2626'
                            }}
                          >
                            <Trash2 size={16} /> Delete
                          </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermSheets;
